import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, Calendar, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = "/logo.png";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHomePage = location.pathname === '/';

  const navLinks = [
    { name: 'Inicio', href: '/' },
    { name: 'Villa', href: '/villa' },
    { name: 'Jardín', href: '/jardin' },
    { name: 'Galería', href: '/galeria' },
    { name: 'Contacto', href: '/contacto' },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomePage
          ? 'bg-[#FAF7F2] shadow-[0_4px_20px_rgba(92,64,51,0.1)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo: arriba del todo en el home NO se muestra, porque el logo
              vive en el hero. Aparece al hacer scroll y en paginas internas.
              Se queda en el DOM aunque este invisible para que el flex no
              recoloque los enlaces; por eso tambien se desactiva el foco y el
              clic mientras no se ve. */}
          <Link
            to="/"
            data-testid="logo-link"
            aria-hidden={isScrolled || !isHomePage ? undefined : true}
            tabIndex={isScrolled || !isHomePage ? undefined : -1}
            className={`flex items-center transition-opacity duration-300 ${
              isScrolled || !isHomePage ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <img
              src={LOGO_URL}
              alt="La Finca - Jardín y Villa"
              className="h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-sm font-medium transition-colors ${
                  isScrolled || !isHomePage
                    ? 'text-[#1A1A1A] hover:text-[#5C4033]'
                    : 'text-white hover:text-[#C8962D]'
                }`}
              >
                {link.name}
              </Link>
            ))}

            <Link to="/reservar">
              <Button
                data-testid="nav-reservar-btn"
                className="bg-[#5C4033] hover:bg-[#3D5A3E] text-white px-6"
              >
                Reservar
              </Button>
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="user-menu-btn"
                    className="border-[#5C4033] text-[#5C4033]"
                  >
                    <User className="h-4 w-4 mr-2" />
                    {user.name.split(' ')[0]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/mis-reservaciones" className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      Mis Reservaciones
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center">
                          <Settings className="h-4 w-4 mr-2" />
                          Panel Admin
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="text-red-600 cursor-pointer"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button
                  variant="outline"
                  data-testid="nav-login-btn"
                  className={`${
                    isScrolled || !isHomePage
                      ? 'border-[#5C4033] text-[#5C4033]'
                      : 'border-white text-white hover:bg-white/10'
                  }`}
                >
                  Iniciar Sesión
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg ${
              isScrolled || !isHomePage ? 'text-[#1A1A1A]' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#FAF7F2] border-t border-[#E0D8CC]">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-[#1A1A1A] hover:text-[#5C4033]"
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/reservar"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block"
            >
              <Button className="w-full bg-[#5C4033] hover:bg-[#3D5A3E] text-white">
                Reservar
              </Button>
            </Link>
            {user ? (
              <>
                <Link
                  to="/mis-reservaciones"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block py-2 text-[#1A1A1A]"
                >
                  Mis Reservaciones
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-2 text-[#1A1A1A]"
                  >
                    Panel Admin
                  </Link>
                )}
                <button
                  onClick={() => {
                    logout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-red-600"
                >
                  Cerrar Sesión
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block"
              >
                <Button variant="outline" className="w-full border-[#5C4033] text-[#5C4033]">
                  Iniciar Sesión
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
